import React from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from 'lib/utils'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
} from 'components/ui/dropdown-menu'

/** Where a jump request wants to land. */
export type TJumpTarget = 'recent' | 'lastWeek' | 'lastMonth' | 'beginning' | 'pickDate'

export interface DateJumpPillProps {
	/** e.g. "Monday, August 3rd" */
	label: string
	onJump: (target: TJumpTarget) => void
	/**
	 * Names what is being moved through, for the trigger's accessible name:
	 * "…Jump to another point in the {noun}".
	 */
	noun?: string
	className?: string
}

/**
 * The date pill that doubles as a jump-to control, as the room chat's day
 * divider popularised and the admin image library now reuses.
 *
 * The accessible name deliberately keeps the "Jump to another" phrasing: it is
 * how the chat e2e finds the pill, and it reads correctly either way.
 */
const DateJumpPill: React.FC<DateJumpPillProps> = ({
	label,
	onJump,
	noun = 'conversation',
	className,
}) => (
	<DropdownMenu>
		<DropdownMenuTrigger asChild>
			<button
				type="button"
				aria-label={`${label}. Jump to another point in the ${noun}`}
				className={cn(
					'mx-3 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white',
					'px-3 py-1 text-[13px] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50',
					className,
				)}
			>
				{label}
				<ChevronDown className="h-3.5 w-3.5 text-gray-400" />
			</button>
		</DropdownMenuTrigger>

		<DropdownMenuContent align="center" className="w-52">
			<DropdownMenuLabel className="text-gray-400">Jump to…</DropdownMenuLabel>
			<DropdownMenuSeparator />
			<DropdownMenuItem onClick={() => onJump('recent')}>Most Recent</DropdownMenuItem>
			<DropdownMenuItem onClick={() => onJump('lastWeek')}>Last Week</DropdownMenuItem>
			<DropdownMenuItem onClick={() => onJump('lastMonth')}>Last Month</DropdownMenuItem>
			<DropdownMenuItem onClick={() => onJump('beginning')}>The very beginning</DropdownMenuItem>
			<DropdownMenuSeparator />
			<DropdownMenuItem onClick={() => onJump('pickDate')}>
				Jump to a specific date…
			</DropdownMenuItem>
		</DropdownMenuContent>
	</DropdownMenu>
)

export default DateJumpPill
